
import { GenericStoreEnhancer, StoreEnhancerStoreCreator, Reducer, Action, AnyAction, Dispatch } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { Facet, IFaceted } from '../../models/Facet';
import { IThunkAction } from '../actions/createActionCreator';
import { assign } from '../../utilities/objects';
import { Signal } from '../../utilities/async/Signal';

const nextStoreUpdateFacet = new Facet<boolean>('nextStoreUpdate');

function injectNextStoreUpdate<S>(payload: {
    promise: Promise<S>;
    action: ThunkAction<{}, S, {}, AnyAction>;
}): ThunkAction<{}, S, {}, AnyAction> {
    return (dispatch: Dispatch<S>, getState: () => S, context: {}) => {
        return payload.action(dispatch, getState, {
            ...context,
            nextStoreUpdate: payload.promise
        });
    };
}

/**
 * @public
 */
export interface INextStoreUpdateContext<S> {
    nextStoreUpdate: Promise<S>;
}

/**
 * @public
 */
export function waitForNextStoreUpdate<S, C extends INextStoreUpdateContext<S>>(): IThunkAction<Promise<S>, S, C> {
    return assign(async (dispatch: Dispatch<S>, getState: () => S, context: C) => {
        return await context.nextStoreUpdate;
    }, {
            ...nextStoreUpdateFacet.pack(true)
        });
}

/**
 * @public
 */
export const nextStoreUpdateEnhancer: GenericStoreEnhancer = <S>(next: StoreEnhancerStoreCreator<S>): StoreEnhancerStoreCreator<S> => {
    return (reducer: Reducer<S>, initialState: S | undefined) => {
        const {
            dispatch: nextDispatch,
            ...store
        } = next(reducer, initialState);

        let previousState = store.getState();

        let {
            resolve,
            promise
        } = new Signal<S>();

        store.subscribe(() => {
            const currentResolve = resolve;

            // Reset the signal before resolving the current one.
            // This will ensure all subsequent 'wait for next store update' actions
            // get queued for the one after this.
            ({
                resolve,
                promise
            } = new Signal<S>());

            currentResolve(previousState);

            previousState = store.getState();
        });

        const dispatch: ThunkDispatch<S, {}, AnyAction> = <A extends Action>(action: A | ThunkAction<{}, S, {}, AnyAction>) => {
            const thunkDispatch = nextDispatch as ThunkDispatch<S, {}, AnyAction>;

            if (typeof action === 'function') {
                const nextStoreUpdate = nextStoreUpdateFacet.unpack(<IFaceted><{}>action);

                if (nextStoreUpdate) {
                    return thunkDispatch(injectNextStoreUpdate({
                        action: action,
                        promise: promise
                    }));
                } else {
                    return thunkDispatch(action);
                }
            } else {
                return thunkDispatch(action);
            }
        };

        return {
            ...store,
            dispatch: dispatch
        };
    };
};
